import type { ReactNode } from "react"
import { Navigate, useLocation } from "react-router"

import { LoadingState } from "@/components/kit/states"
import { PermissionDenied } from "@/components/kit/PermissionDenied"
import { enrolPath } from "@/lib/safe-from"
import { asArray, isMoneyVisible, routeAllowed, type RouteAccess } from "./permissions"
import { useMe } from "./session"
import { mustEnrolTotp } from "./staff"

/**
 * The route guard (LLD §6.5), under the session gate, in this order:
 * - `me` on its way: a skeleton. Nothing is decided yet, so no denial and
 *   no redirect fires early (a standalone `/billing` must not reach `/plans`);
 * - a staff member without TOTP: `/enrol-totp`, keeping the destination.
 *   The backend refuses them every admin route (FR-DSH-PUB-08); a typed URL
 *   must not get round the enrolment screen either;
 * - a money route in standalone: `notFound`, as for an unknown path. The
 *   route does not exist in that mode (LLD §7.1), so it is not "denied";
 * - otherwise `routeAllowed`, or `PermissionDenied` naming what is missing.
 *
 * `notFound` is the router's own screen, passed in so `auth/` does not
 * import the app layer.
 */
export function RequirePermission({
  route,
  notFound,
  children,
}: {
  route: RouteAccess
  notFound: ReactNode
  children: ReactNode
}) {
  const { data: me } = useMe()
  const { pathname, search, hash } = useLocation()
  if (!me) return <LoadingState variant="detail" />
  if (mustEnrolTotp(me)) return <Navigate to={enrolPath(pathname + search + hash)} replace />
  if (route.money && !isMoneyVisible(me.mode)) return notFound
  if (!routeAllowed(route, me)) return <PermissionDenied required={asArray(route.permission)} />
  return children
}
